import { useMemo, memo, useState, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useGeography } from '@/hooks/useDashboardAnalytics';
import { Skeleton } from '@/components/ui/skeleton';
import { Globe, Maximize2, Minimize2 } from 'lucide-react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTheme } from 'next-themes';
import {
  ComposableMap,
  Geographies,
  Geography,
  Marker,
  ZoomableGroup,
} from 'react-simple-maps';

interface GeographyChartProps {
  days?: number;
  className?: string;
}

// Координаты столиц [долгота, широта] — для маркеров на карте.
// Полное покрытие ISO-3166-1 alpha-2, чтобы маркер ставился для любой страны.
export const COUNTRY_COORDS: Record<string, [number, number]> = {
  AD: [1.5218, 42.5063], AE: [54.3705, 24.4539], AF: [69.2075, 34.5553],
  AG: [-61.8456, 17.1175], AI: [-63.0501, 18.2206], AL: [19.8187, 41.3275],
  AM: [44.5012, 40.1872], AO: [13.2343, -8.8390], AR: [-58.3816, -34.6037],
  AS: [-170.7020, -14.2756], AT: [16.3738, 48.2082], AU: [149.1300, -35.2809],
  AW: [-70.0270, 12.5092], AX: [19.9156, 60.1785], AZ: [49.8671, 40.4093],
  BA: [18.4131, 43.8563], BB: [-59.6105, 13.1132], BD: [90.4125, 23.8103],
  BE: [4.3517, 50.8503], BF: [-1.5197, 12.3714], BG: [23.3219, 42.6977],
  BH: [50.5860, 26.2285], BI: [29.3644, -3.3614], BJ: [2.6323, 6.4969],
  BL: [-62.8333, 17.9000], BM: [-64.7833, 32.2942], BN: [114.9424, 4.9031],
  BO: [-68.1193, -16.4897], BQ: [-68.2624, 12.1784], BR: [-47.8825, -15.7942],
  BS: [-77.3963, 25.0480], BT: [89.6390, 27.4728], BW: [25.9201, -24.6282],
  BY: [27.5615, 53.9045], BZ: [-88.7569, 17.2510], CA: [-75.6972, 45.4215],
  CC: [96.8333, -12.1667], CD: [15.2663, -4.4419], CF: [18.5582, 4.3947],
  CG: [15.2429, -4.2634], CH: [7.4474, 46.9480], CI: [-5.2767, 6.8276],
  CK: [-159.7777, -21.2073], CL: [-70.6693, -33.4489], CM: [11.5021, 3.8480],
  CN: [116.4074, 39.9042], CO: [-74.0721, 4.7110], CR: [-84.0907, 9.9281],
  CU: [-82.3666, 23.1136], CV: [-23.5133, 14.9330], CW: [-68.9335, 12.1696],
  CX: [105.6904, -10.4475], CY: [33.3823, 35.1856], CZ: [14.4378, 50.0755],
  DE: [13.4050, 52.5200], DJ: [43.1456, 11.5721], DK: [12.5683, 55.6761],
  DM: [-61.3870, 15.3092], DO: [-69.9312, 18.4861], DZ: [3.0588, 36.7538],
  EC: [-78.4678, -0.1807], EE: [24.7536, 59.4370], EG: [31.2357, 30.0444],
  EH: [-13.2033, 27.1536], ER: [38.9251, 15.3229], ES: [-3.7038, 40.4168],
  ET: [38.7578, 9.0320], FI: [24.9384, 60.1699], FJ: [178.4419, -18.1248],
  FK: [-57.8540, -51.6976], FM: [158.1610, 6.9248], FO: [-6.7720, 62.0079],
  FR: [2.3522, 48.8566], GA: [9.4673, 0.4162], GB: [-0.1276, 51.5074],
  GD: [-61.7486, 12.0561], GE: [44.8271, 41.7151], GF: [-52.3260, 4.9224],
  GG: [-2.5360, 49.4566], GH: [-0.1870, 5.6037], GI: [-5.3536, 36.1408],
  GL: [-51.7216, 64.1836], GM: [-16.5790, 13.4549], GN: [-13.7122, 9.6412],
  GP: [-61.5340, 16.2650], GQ: [8.7833, 3.7500], GR: [23.7275, 37.9838],
  GT: [-90.5133, 14.6349], GU: [144.7937, 13.4443], GW: [-15.5980, 11.8037],
  GY: [-58.1553, 6.8013], HK: [114.1694, 22.3193], HN: [-87.1921, 14.0723],
  HR: [15.9819, 45.8150], HT: [-72.3074, 18.5944], HU: [19.0402, 47.4979],
  ID: [106.8456, -6.2088], IE: [-6.2603, 53.3498], IL: [35.2137, 31.7683],
  IM: [-4.4861, 54.1500], IN: [77.2090, 28.6139], IO: [72.4297, -7.3133],
  IQ: [44.3661, 33.3152], IR: [51.3890, 35.6892], IS: [-21.8954, 64.1466],
  IT: [12.4964, 41.9028], JE: [-2.1058, 49.1858], JM: [-76.7936, 17.9712],
  JO: [35.9304, 31.9454], JP: [139.6917, 35.6895], KE: [36.8219, -1.2921],
  KG: [74.5698, 42.8746], KH: [104.9282, 11.5564], KI: [172.9717, 1.3290],
  KM: [43.2551, -11.7172], KN: [-62.7177, 17.3026], KP: [125.7625, 39.0392],
  KR: [126.9780, 37.5665], KW: [47.9774, 29.3759], KY: [-81.3833, 19.3133],
  KZ: [71.4491, 51.1694], LA: [102.6000, 17.9757], LB: [35.5018, 33.8938],
  LC: [-60.9789, 14.0101], LI: [9.5215, 47.1410], LK: [79.8612, 6.9271],
  LR: [-10.8014, 6.3007], LS: [27.4869, -29.3151], LT: [25.2797, 54.6872],
  LU: [6.1296, 49.6116], LV: [24.1052, 56.9496], LY: [13.1913, 32.8872],
  MA: [-6.8498, 33.9716], MC: [7.4246, 43.7384], MD: [28.8353, 47.0105],
  ME: [19.2636, 42.4304], MF: [-63.0501, 18.0708], MG: [47.5079, -18.8792],
  MH: [171.3803, 7.1164], MK: [21.4254, 41.9981], ML: [-8.0029, 12.6392],
  MM: [96.1951, 16.8409], MN: [106.9177, 47.8864], MO: [113.5439, 22.1987],
  MP: [145.7546, 15.1850], MQ: [-61.0242, 14.6415], MR: [-15.9785, 18.0735],
  MS: [-62.2104, 16.7425], MT: [14.5146, 35.8989], MU: [57.5012, -20.1609],
  MV: [73.5093, 4.1755], MW: [33.7741, -13.9626], MX: [-99.1332, 19.4326],
  MY: [101.6869, 3.1390], MZ: [32.5732, -25.9692], NA: [17.0832, -22.5609],
  NC: [166.4581, -22.2758], NE: [2.1098, 13.5117], NF: [167.9547, -29.0408],
  NG: [7.4951, 9.0579], NI: [-86.2362, 12.1149], NL: [4.9041, 52.3676],
  NO: [10.7522, 59.9139], NP: [85.3240, 27.7172], NR: [166.9209, -0.5477],
  NU: [-169.8672, -19.0544], NZ: [174.7762, -41.2865], OM: [58.4059, 23.5880],
  PA: [-79.5199, 8.9824], PE: [-77.0428, -12.0464], PF: [-149.5667, -17.5333],
  PG: [147.1797, -9.4438], PH: [120.9842, 14.5995], PK: [73.0479, 33.6844],
  PL: [21.0122, 52.2297], PM: [-56.1734, 46.7783], PN: [-130.1015, -25.0667],
  PR: [-66.1057, 18.4655], PS: [35.2034, 31.9038], PT: [-9.1393, 38.7223],
  PW: [134.6242, 7.5000], PY: [-57.5759, -25.2637], QA: [51.5310, 25.2854],
  RE: [55.4504, -20.8789], RO: [26.1025, 44.4268], RS: [20.4489, 44.7866],
  RU: [37.6173, 55.7558], RW: [30.0619, -1.9441], SA: [46.6753, 24.7136],
  SB: [159.9729, -9.4280], SC: [55.4915, -4.6796], SD: [32.5599, 15.5007],
  SE: [18.0686, 59.3293], SG: [103.8198, 1.3521], SH: [-5.7167, -15.9650],
  SI: [14.5058, 46.0569], SJ: [15.6469, 78.2232], SK: [17.1077, 48.1486],
  SL: [-13.2317, 8.4657], SM: [12.4578, 43.9424], SN: [-17.4677, 14.7167],
  SO: [45.3182, 2.0469], SR: [-55.2038, 5.8520], SS: [31.5825, 4.8594],
  ST: [6.6131, 0.3302], SV: [-89.2182, 13.6929], SX: [-63.0548, 18.0425],
  SY: [36.2765, 33.5138], SZ: [31.1367, -26.3054], TC: [-71.1389, 21.4664],
  TD: [15.0444, 12.1348], TF: [70.2167, -49.3500], TG: [1.2255, 6.1725],
  TH: [100.5018, 13.7563], TJ: [68.7870, 38.5598], TK: [-171.8556, -9.2002],
  TL: [125.5603, -8.5569], TM: [58.3833, 37.9601], TN: [10.1815, 36.8065],
  TO: [-175.2018, -21.1394], TR: [32.8597, 39.9334], TT: [-61.5167, 10.6549],
  TV: [179.1942, -8.5211], TW: [121.5654, 25.0330], TZ: [35.7395, -6.1630],
  UA: [30.5234, 50.4501], UG: [32.5825, 0.3476], US: [-77.0369, 38.9072],
  UY: [-56.1645, -34.9011], UZ: [69.2401, 41.2995], VA: [12.4534, 41.9029],
  VC: [-61.2872, 13.1600], VE: [-66.9036, 10.4806], VG: [-64.6230, 18.4207],
  VI: [-64.8963, 18.3358], VN: [105.8342, 21.0278], VU: [168.3219, -17.7333],
  WF: [-178.1165, -14.2938], WS: [-171.7513, -13.8333], XK: [21.1655, 42.6629],
  YE: [44.2067, 15.3694], YT: [45.2280, -12.7806], ZA: [28.0473, -25.7479],
  ZM: [28.3228, -15.3875], ZW: [31.0530, -17.8252],
};

const GEO_URL = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

// ISO-3166-1 alpha-2 → numeric. world-atlas использует numeric-коды как geo.id;
// полное покрытие нужно, чтобы хороплет подсвечивал любую страну из статистики.
const ALPHA2_TO_NUMERIC: Record<string, string> = {
  AD: '020', AE: '784', AF: '004', AG: '028', AI: '660', AL: '008', AM: '051',
  AO: '024', AQ: '010', AR: '032', AS: '016', AT: '040', AU: '036', AW: '533',
  AX: '248', AZ: '031', BA: '070', BB: '052', BD: '050', BE: '056', BF: '854',
  BG: '100', BH: '048', BI: '108', BJ: '204', BL: '652', BM: '060', BN: '096',
  BO: '068', BQ: '535', BR: '076', BS: '044', BT: '064', BV: '074', BW: '072',
  BY: '112', BZ: '084', CA: '124', CC: '166', CD: '180', CF: '140', CG: '178',
  CH: '756', CI: '384', CK: '184', CL: '152', CM: '120', CN: '156', CO: '170',
  CR: '188', CU: '192', CV: '132', CW: '531', CX: '162', CY: '196', CZ: '203',
  DE: '276', DJ: '262', DK: '208', DM: '212', DO: '214', DZ: '012', EC: '218',
  EE: '233', EG: '818', EH: '732', ER: '232', ES: '724', ET: '231', FI: '246',
  FJ: '242', FK: '238', FM: '583', FO: '234', FR: '250', GA: '266', GB: '826',
  GD: '308', GE: '268', GF: '254', GG: '831', GH: '288', GI: '292', GL: '304',
  GM: '270', GN: '324', GP: '312', GQ: '226', GR: '300', GS: '239', GT: '320',
  GU: '316', GW: '624', GY: '328', HK: '344', HM: '334', HN: '340', HR: '191',
  HT: '332', HU: '348', ID: '360', IE: '372', IL: '376', IM: '833', IN: '356',
  IO: '086', IQ: '368', IR: '364', IS: '352', IT: '380', JE: '832', JM: '388',
  JO: '400', JP: '392', KE: '404', KG: '417', KH: '116', KI: '296', KM: '174',
  KN: '659', KP: '408', KR: '410', KW: '414', KY: '136', KZ: '398', LA: '418',
  LB: '422', LC: '662', LI: '438', LK: '144', LR: '430', LS: '426', LT: '440',
  LU: '442', LV: '428', LY: '434', MA: '504', MC: '492', MD: '498', ME: '499',
  MF: '663', MG: '450', MH: '584', MK: '807', ML: '466', MM: '104', MN: '496',
  MO: '446', MP: '580', MQ: '474', MR: '478', MS: '500', MT: '470', MU: '480',
  MV: '462', MW: '454', MX: '484', MY: '458', MZ: '508', NA: '516', NC: '540',
  NE: '562', NF: '574', NG: '566', NI: '558', NL: '528', NO: '578', NP: '524',
  NR: '520', NU: '570', NZ: '554', OM: '512', PA: '591', PE: '604', PF: '258',
  PG: '598', PH: '608', PK: '586', PL: '616', PM: '666', PN: '612', PR: '630',
  PS: '275', PT: '620', PW: '585', PY: '600', QA: '634', RE: '638', RO: '642',
  RS: '688', RU: '643', RW: '646', SA: '682', SB: '090', SC: '690', SD: '729',
  SE: '752', SG: '702', SH: '654', SI: '705', SJ: '744', SK: '703', SL: '694',
  SM: '674', SN: '686', SO: '706', SR: '740', SS: '728', ST: '678', SV: '222',
  SX: '534', SY: '760', SZ: '748', TC: '796', TD: '148', TF: '260', TG: '768',
  TH: '764', TJ: '762', TK: '772', TL: '626', TM: '795', TN: '788', TO: '776',
  TR: '792', TT: '780', TV: '798', TW: '158', TZ: '834', UA: '804', UG: '800',
  UM: '581', US: '840', UY: '858', UZ: '860', VA: '336', VC: '670', VE: '862',
  VG: '092', VI: '850', VN: '704', VU: '548', WF: '876', WS: '882', XK: '983',
  YE: '887', YT: '175', ZA: '710', ZM: '894', ZW: '716',
};

// world-atlas отдаёт geo.id без ведущих нулей ('4' вместо '004') — ключи нормализуем
// через parseInt, чтобы '004'/'4'/4 матчились одинаково.
const normalizeNumericCode = (value: string | number): string => {
  const parsed = parseInt(String(value), 10);
  return Number.isNaN(parsed) ? '' : String(parsed);
};

const NUMERIC_TO_ALPHA2 = new Map(
  Object.entries(ALPHA2_TO_NUMERIC).map(([alpha, num]) => [normalizeNumericCode(num), alpha])
);

// Цветовые схемы для темы
const THEME_COLORS = {
  dark: {
    landDefault: '#1e293b',
    landHighlighted: '#1e40af',
    landHover: '#334155',
    landHighlightedHover: '#2563eb',
    stroke: '#475569',
    markerFill: '#3b82f6',
    markerGlow: '#3b82f6',
    markerText: '#ffffff',
    labelText: '#94a3b8',
  },
  light: {
    landDefault: '#e2e8f0',
    landHighlighted: '#93c5fd',
    landHover: '#cbd5e1',
    landHighlightedHover: '#60a5fa',
    stroke: '#94a3b8',
    markerFill: '#2563eb',
    markerGlow: '#3b82f6',
    markerText: '#ffffff',
    labelText: '#64748b',
  },
};

interface MarkerData {
  countryCode: string;
  country: string;
  users: number;
  percentage: number;
  coordinates: [number, number];
}

interface MapMarkerProps {
  marker: MarkerData;
  maxUsers: number;
  colors: typeof THEME_COLORS.dark;
  zoom: number;
  isHovered: boolean;
  onHover: (countryCode: string | null) => void;
}

const MapMarker = memo(function MapMarker({ marker, maxUsers, colors, zoom, isHovered, onHover }: MapMarkerProps) {
  const baseSize = Math.max(6, Math.min(16, 6 + (marker.users / maxUsers) * 10));
  // При наведении (с карты или из легенды) — увеличиваем маркер для акцента.
  const hoverScale = isHovered ? 1.45 : 1;
  const size = (baseSize / zoom) * hoverScale;
  const fontSize = (Math.max(5, baseSize * 0.5) / zoom) * hoverScale;
  const labelFontSize = (6 / zoom) * (isHovered ? 1.5 : 1);
  const strokeWidth = 1 / zoom;

  return (
    <Marker
      coordinates={marker.coordinates}
      onMouseEnter={() => onHover(marker.countryCode)}
      onMouseLeave={() => onHover(null)}
      style={{ default: { cursor: 'pointer' }, hover: { cursor: 'pointer' }, pressed: {} }}
    >
      {/* Внешнее свечение */}
      <circle
        r={size}
        fill={colors.markerGlow}
        fillOpacity={isHovered ? 0.3 : 0.15}
        stroke={colors.markerGlow}
        strokeWidth={isHovered ? strokeWidth * 2 : strokeWidth}
        strokeOpacity={isHovered ? 0.9 : 0.5}
        style={{ transition: 'r 0.15s ease, fill-opacity 0.15s ease' }}
      />
      {/* Основной круг */}
      <circle
        r={size * 0.6}
        fill={colors.markerFill}
        fillOpacity={isHovered ? 1 : 0.9}
        style={{ transition: 'r 0.15s ease' }}
      />
      {/* Число белым по центру */}
      <text
        textAnchor="middle"
        dominantBaseline="central"
        style={{
          fontSize: `${fontSize}px`,
          fontWeight: 700,
          fill: colors.markerText,
          pointerEvents: 'none',
        }}
      >
        {marker.users}
      </text>
      {/* Название страны снизу */}
      <text
        textAnchor="middle"
        y={size + 8 / zoom}
        style={{
          fontSize: `${labelFontSize}px`,
          fontWeight: isHovered ? 700 : 500,
          fill: isHovered ? colors.markerFill : colors.labelText,
          pointerEvents: 'none',
        }}
      >
        {marker.country}
      </text>
    </Marker>
  );
});

interface GeographyMapContentProps {
  distribution: Array<{ countryCode: string; country: string; users: number; percentage: number }>;
  countryDataMap: Map<string, { users: number; percentage: number }>;
  markers: MarkerData[];
  maxUsers: number;
  colors: typeof THEME_COLORS.dark;
  // Высота области карты. number → фикс. px (карточка дашборда); 'fill' → растянуть на всю
  // высоту родителя (fullscreen 16:9, viewBox тоже 16:9 чтобы карта не искажалась).
  mapHeight: number | 'fill';
}

// viewBox карты: в обычном режиме высота = mapHeight (исторический скейл),
// в режиме 'fill' — 16:9 (500×281), чтобы при растяжении на весь блок не было искажений.
const VIEWBOX_WIDTH = 500;
const FILL_VIEWBOX_HEIGHT = Math.round((VIEWBOX_WIDTH * 9) / 16);

const GeographyMapContent = memo(function GeographyMapContent({
  distribution,
  countryDataMap,
  markers,
  maxUsers,
  colors,
  mapHeight,
}: GeographyMapContentProps) {
  const [currentZoom, setCurrentZoom] = useState(1);
  // Страна под наведением (с карты или из легенды) — общий источник подсветки.
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);

  const handleMove = useCallback((position: { x: number; y: number; zoom: number }) => {
    setCurrentZoom(position.zoom);
  }, []);

  const isFill = mapHeight === 'fill';
  const viewBoxHeight = isFill ? FILL_VIEWBOX_HEIGHT : mapHeight;

  return (
    <div className={isFill ? 'flex h-full w-full flex-col' : undefined}>
      <div
        className={isFill ? 'flex-1 min-h-0' : undefined}
        style={isFill ? { width: '100%' } : { width: '100%', height: mapHeight }}
      >
        <ComposableMap
          projection="geoMercator"
          projectionConfig={{
            scale: 120,
            center: [55, 40],
          }}
          width={VIEWBOX_WIDTH}
          height={viewBoxHeight}
          style={{ width: '100%', height: '100%' }}
        >
          <ZoomableGroup center={[55, 40]} zoom={1} minZoom={0.8} maxZoom={4} onMove={handleMove}>
            <Geographies geography={GEO_URL}>
              {({ geographies }) =>
                geographies.map((geo) => {
                  const geoId = normalizeNumericCode(geo.id as string | number);
                  const matchedCode = NUMERIC_TO_ALPHA2.get(geoId);
                  const hasData = matchedCode ? countryDataMap.has(matchedCode) : false;
                  const isHovered = hasData && matchedCode === hoveredCountry;

                  return (
                    <Geography
                      key={geo.rsmKey}
                      geography={geo}
                      onMouseEnter={() => hasData && matchedCode && setHoveredCountry(matchedCode)}
                      onMouseLeave={() => setHoveredCountry(null)}
                      style={{
                        default: {
                          fill: isHovered
                            ? colors.landHighlightedHover
                            : (hasData ? colors.landHighlighted : colors.landDefault),
                          fillOpacity: isHovered ? 0.85 : (hasData ? 0.4 : 1),
                          stroke: isHovered ? colors.markerFill : colors.stroke,
                          strokeWidth: isHovered ? 1 : 0.5,
                          outline: 'none',
                          transition: 'fill-opacity 0.15s ease, fill 0.15s ease',
                          cursor: hasData ? 'pointer' : 'default',
                        },
                        hover: {
                          fill: hasData ? colors.landHighlightedHover : colors.landHover,
                          fillOpacity: hasData ? 0.85 : 1,
                          stroke: hasData ? colors.markerFill : colors.stroke,
                          strokeWidth: hasData ? 1 : 0.8,
                          outline: 'none',
                          cursor: hasData ? 'pointer' : 'default',
                        },
                        pressed: {
                          fill: hasData ? colors.landHighlightedHover : colors.landDefault,
                          fillOpacity: hasData ? 0.9 : 1,
                          outline: 'none',
                        },
                      }}
                    />
                  );
                })
              }
            </Geographies>

            {markers.map((marker) => (
              <MapMarker
                key={marker.countryCode}
                marker={marker}
                maxUsers={maxUsers}
                colors={colors}
                zoom={currentZoom}
                isHovered={marker.countryCode === hoveredCountry}
                onHover={setHoveredCountry}
              />
            ))}
          </ZoomableGroup>
        </ComposableMap>
      </div>

      {/* Легенда — наведение подсвечивает страну на карте */}
      <div className={isFill ? 'px-4 pt-2 shrink-0' : 'px-4 pt-2'}>
        <div className="flex flex-wrap gap-x-4 gap-y-1">
          {distribution.map((item) => {
            const isHovered = item.countryCode === hoveredCountry;
            return (
              <div
                key={item.countryCode}
                className="flex items-center gap-1.5 text-xs cursor-pointer rounded px-1 -mx-1 transition-colors"
                style={{ backgroundColor: isHovered ? `${colors.markerFill}1a` : 'transparent' }}
                onMouseEnter={() => setHoveredCountry(item.countryCode)}
                onMouseLeave={() => setHoveredCountry(null)}
              >
                <span
                  className="inline-block rounded-full transition-all"
                  style={{
                    backgroundColor: colors.markerFill,
                    width: isHovered ? '0.625rem' : '0.5rem',
                    height: isHovered ? '0.625rem' : '0.5rem',
                  }}
                />
                <span className={isHovered ? 'text-foreground' : 'text-muted-foreground'}>
                  {item.country}: <span className="font-medium text-foreground">{item.users}</span> ({item.percentage}%)
                </span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
});

export function GeographyChart({ days = 30, className }: GeographyChartProps) {
  const { data, isLoading } = useGeography(days);
  const { resolvedTheme } = useTheme();
  const [isFullscreen, setIsFullscreen] = useState(false);

  const isDark = resolvedTheme === 'dark';
  const colors = isDark ? THEME_COLORS.dark : THEME_COLORS.light;

  const distribution = data?.distribution ?? [];

  const countryDataMap = useMemo(() => {
    const map = new Map<string, { users: number; percentage: number }>();
    for (const item of (data?.distribution ?? [])) {
      map.set(item.countryCode, { users: item.users, percentage: item.percentage });
    }
    return map;
  }, [data?.distribution]);

  const markers = useMemo<MarkerData[]>(() => {
    return (data?.distribution ?? [])
      .filter((item) => COUNTRY_COORDS[item.countryCode])
      .map((item) => {
        const coords = COUNTRY_COORDS[item.countryCode];
        return {
          countryCode: item.countryCode,
          country: item.country,
          users: item.users,
          percentage: item.percentage,
          coordinates: coords ?? [0, 0],
        };
      });
  }, [data?.distribution]);

  const maxUsers = useMemo(() => {
    return Math.max(1, ...markers.map((m) => m.users));
  }, [markers]);

  if (isLoading) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">География пользователей</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[280px]">
            <Skeleton className="h-[240px] w-full rounded-lg" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (distribution.length === 0) {
    return (
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Globe className="h-4 w-4" />
            География пользователей
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[280px] text-muted-foreground">
            Нет данных о географии
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className={className}>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <Globe className="h-4 w-4" />
            География пользователей
          </CardTitle>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">
              {data?.totalCountries ?? 0} {distribution.length === 1 ? 'страна' : (distribution.length < 5 ? 'страны' : 'стран')}
            </span>
            <Button
              variant="ghost"
              size="icon"
              className="h-6 w-6"
              onClick={() => setIsFullscreen(true)}
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 pb-4">
          <GeographyMapContent
            distribution={distribution}
            countryDataMap={countryDataMap}
            markers={markers}
            maxUsers={maxUsers}
            colors={colors}
            mapHeight={280}
          />
        </CardContent>
      </Card>

      <Dialog open={isFullscreen} onOpenChange={setIsFullscreen}>
        <DialogContent className="max-w-[95vw] w-[95vw] h-[90vh] flex flex-col p-0">
          <div className="flex items-center justify-between px-6 pt-6 pb-2">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Globe className="h-5 w-5" />
              География пользователей
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">
                {data?.totalCountries ?? 0} {distribution.length === 1 ? 'страна' : (distribution.length < 5 ? 'страны' : 'стран')}
              </span>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8"
                onClick={() => setIsFullscreen(false)}
              >
                <Minimize2 className="h-4 w-4" />
              </Button>
            </div>
          </div>
          <div className="flex-1 min-h-0 px-6 pb-6 flex items-center justify-center">
            {/* На десктопе карта в соотношении 16:9, по центру. aspect-video задаёт пропорцию;
                max-h-full ограничивает по высоте диалога, ширина подстраивается. */}
            <div className="aspect-video max-h-full max-w-full w-auto h-full mx-auto flex">
              <GeographyMapContent
                distribution={distribution}
                countryDataMap={countryDataMap}
                markers={markers}
                maxUsers={maxUsers}
                colors={colors}
                mapHeight="fill"
              />
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
